<template>
  <PageLayout :page-title="$t('nav.about')" banner-key="about">

    <section id="page1" class="aboutDiv1 pd90">
      <div class="wrap">
        <div class="bdDiv">
          <div class="textDiv wow fadeInUp">
            <div class="titleDiv" v-if="introHeadline || introSubtitle">
              <span v-if="introHeadline" class="f36 bold">{{ introHeadline }}</span>
              <p v-if="introSubtitle" class="f20">{{ introSubtitle }}</p>
            </div>
            <div v-if="introHtml" class="p f18" v-html="introHtml"></div>
          </div>
          <div class="dataDiv wow fadeInUp">
            <ul class="flexbet">
              <li v-for="(stat, idx) in stats" :key="idx">
                <div class="conDiv">
                  <div class="num">
                    <span class="counter f80">{{ stat.display }}</span>
                    <sub v-if="stat.suffix" :class="stat.suffixClass || 'f33'">{{ stat.suffix }}</sub>
                  </div>
                  <p>{{ stat.label }}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section id="page6" class="history02 pd90">
      <div class="wrap">
        <div class="indexTitle tc wow fadeInUp"><span class="f48">{{ sectionTitle('history') }}</span></div>
        <div class="hd">
          <div
            v-for="(stage, idx) in historyStages"
            :key="stage.slug"
            class="item"
            :class="{ cur: activeHistoryStage === idx }"
          >
            <div class="infor">
              <div class="tit common_tit f36">{{ stage.title }} {{ stage.subtitle }}</div>
              <div class="con f18">{{ stage.summary }}</div>
            </div>
          </div>
        </div>
        <div class="bd">
          <div class="indicators flexbet">
            <div
              v-for="(stage, idx) in historyStages"
              :key="'ind-' + stage.slug"
              class="indi_item"
              :class="{ active: activeHistoryStage === idx, cur: activeHistoryStage === idx }"
              @click="activeHistoryStage = idx"
            >
              <div class="line"><div class="progress" :style="{ width: activeHistoryStage === idx ? '100%' : '0' }"></div></div>
              <div class="txt">{{ stage.title }}</div>
            </div>
          </div>
          <div class="stages">
            <div class="stage_item">
              <div class="cont">
                <div class="years">
                  <div
                    v-for="(year, yIdx) in currentHistoryYears"
                    :key="year.year"
                    class="year_item"
                    :class="{ on: activeHistoryYear === yIdx }"
                    @mouseenter="activeHistoryYear = yIdx"
                  >
                    <div class="intro_box">
                      <div class="infor">
                        <div class="tit">{{ year.year }}</div>
                        <div class="con">{{ year.text }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="rdSlides.length" id="page8" class="aboutDiv3 pd90">
      <div class="wrap">
        <div class="indexTitle tc wow fadeInUp">
          <span class="f48">{{ sectionTitle('rd') }}</span>
          <p v-if="rdContentText" class="f18">{{ rdContentText }}</p>
        </div>
        <div ref="aboutRdSwiper" class="bdDiv rd-carousel">
          <div class="rd-carousel-viewport">
            <div class="rd-carousel-track" :style="rdTrackStyle">
              <div
                v-for="(slide, idx) in rdSlides"
                :key="'rd-' + idx + '-' + slide.image"
                class="rd-carousel-slide"
                :class="{ 'is-active': idx === activeRdIndex }"
              >
                <div class="img">
                  <div class="imgDiv imgOver">
                    <div class="pic">
                      <img v-if="slide.image" :src="$media(slide.image)" :alt="slide.title">
                    </div>
                  </div>
                  <div class="titleDiv">{{ slide.title }}</div>
                </div>
              </div>
            </div>
          </div>
          <button type="button" class="swiper-button-prev flexcen rd-carousel-nav" aria-label="Previous" @click.stop="onRdNav('prev')"><i class="font_family icon-zuojiantou"></i></button>
          <button type="button" class="swiper-button-next flexcen rd-carousel-nav" aria-label="Next" @click.stop="onRdNav('next')"><i class="font_family icon-youjiantou"></i></button>
        </div>
      </div>
    </section>

    <section v-if="certificates.length" id="page9" class="aboutDiv8 pd90">
      <div class="wrap">
        <div class="indexTitle tc wow fadeInUp">
          <span class="f48">{{ sectionTitle('honors') }}</span>
          <p v-if="honorsIntroText" class="f18">{{ honorsIntroText }}</p>
        </div>
        <div ref="aboutHonorsSwiper" class="bdDiv">
          <div class="swiper-container">
            <div class="swiper-wrapper">
              <div v-for="cert in certificates" :key="cert.id" class="swiper-slide">
                <div class="img">
                  <div class="imgDiv imgObj">
                    <div class="pic">
                      <img v-if="cert.image" :src="$media(cert.image)" :alt="cert.title">
                    </div>
                  </div>
                  <div class="h">{{ cert.title }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="swiper-button-prev flexcen"><i class="font_family icon-zuojiantou"></i></div>
          <div class="swiper-button-next flexcen"><i class="font_family icon-youjiantou"></i></div>
        </div>
      </div>
    </section>

    <section id="page11" class="joinDiv pd90">
      <div class="wrap">
        <div class="indexTitle tc wow fadeInUp"><span class="f48">{{ sectionTitle('careers') }}</span></div>
        <div class="bdDiv">
          <div v-if="careersIntro" class="careers-intro f18" v-html="$cleanBrand(careersIntro)"></div>
          <div class="bh flexbet">
            <div class="item">{{ $t('about.jobTitle') }}</div>
            <div class="item">{{ $t('about.jobLocation') }}</div>
            <div class="item">{{ $t('about.jobDept') }}</div>
            <div class="item">{{ $t('about.jobType') }}</div>
            <div class="item">{{ $t('about.jobDate') }}</div>
          </div>
          <ul>
            <li v-for="(job, idx) in jobItems" :key="idx">
              <div class="lh flexbet">
                <div class="item">{{ job.title }}</div>
                <div class="item">{{ job.location }}</div>
                <div class="item">{{ job.dept }}</div>
                <div class="item">{{ job.type }}</div>
                <div class="item">{{ job.date }}</div>
              </div>
            </li>
          </ul>
          <p v-if="!jobItems.length" style="text-align:center;color:#999;padding:32px 0">{{ $t('common.contentSoon') }}</p>
        </div>
      </div>
    </section>

  </PageLayout>
</template>

<script>
import PageLayout from '@/components/hirays/PageLayout.vue'
import { listCompanyInfo, listCertificate, listSiteStat, listSiteNavItem } from '@/utils/http'
import { unwrapRows, resolveImageUrl, mapSiteStats, parsePicUrlList, parseCultureLines, parseManufacturingPanels, parseJobItemsFromHtml, mapCompanyInfoRowToJob, parseHistoryStages } from '@/utils/content'
import { ABOUT_NAV_LINKS } from '@/constants/hiraysHomeStatic'
import { destroySwiper, initAboutDiv8 } from '@/utils/swiperHelper'

export default {
  components: { PageLayout },
  data() {
    return {
      introContent: '',
      introSubtitleText: '',
      honorsContent: '',
      certificates: [],
      stats: [],
      historyStages: [],
      cultureItems: [],
      rdSlides: [],
      rdContentText: '',
      activeHistoryStage: 0,
      activeHistoryYear: 0,
      jobItems: [],
      careersIntro: '',
      sectionTitles: {},
      navSectionTitles: {},
      swipers: [],
      honorsSwiper: null,
      activeRdIndex: 0,
      rdAutoplayTimer: null
    }
  },
  computed: {
    rdTrackStyle() {
      return {
        transform: 'translate3d(-' + (this.activeRdIndex * 100) + '%, 0, 0)',
        transitionDuration: '800ms'
      }
    },
    introHeadline() {
      return this.sectionTitles.intro || this.navSectionTitles.intro || ''
    },
    introSubtitle() {
      return this.introSubtitleText || ''
    },
    introHtml() {
      if (!this.introContent) return ''
      return this.$cleanBrand(this.introContent)
    },
    honorsHtml() {
      if (!this.honorsContent) return ''
      return this.$cleanBrand(this.honorsContent)
    },
    honorsIntroText() {
      return this.stripHtml(this.honorsHtml)
    },
    currentHistoryYears() {
      const stage = this.historyStages[this.activeHistoryStage]
      return stage ? stage.years : []
    }
  },
  watch: {
    '$route.hash'() {
      this.scrollToHash()
    },
    activeHistoryStage() {
      this.activeHistoryYear = 0
    },
    certificates: {
      handler(val) {
        if (val && val.length) this.scheduleHonorsSwiperInit()
      }
    }
  },
  mounted() {
    this.load()
  },
  beforeDestroy() {
    this.stopRdAutoplay()
    this.destroySwipers()
  },
  methods: {
    reloadCmsData() {
      this.load()
    },

    sectionTitle(slug) {
      if (this.sectionTitles[slug]) return this.sectionTitles[slug]
      if (this.navSectionTitles[slug]) return this.navSectionTitles[slug]
      const item = ABOUT_NAV_LINKS.find(a => a.slug === slug)
      return item ? this.$navLabel('aboutNav', item.slug, item.name) : slug
    },

    async loadAboutNav() {
      try {
        const res = await listSiteNavItem({ navGroup: 1, status: 1, pageNum: 1, pageSize: 20 }, this.$i18n.locale)
        const titles = {}
        unwrapRows(res).forEach(row => {
          const name = this.$cleanBrand(row.itemName || '')
          if (!name) return
          if (row.itemSlug === 'company-intro') titles.intro = name
          else if (row.itemSlug) titles[row.itemSlug] = name
        })
        this.navSectionTitles = titles
      } catch (e) {
        this.navSectionTitles = {}
      }
    },

    scrollToHash() {
      this.$nextTick(() => {
        const hash = this.$route.hash
        if (!hash) return
        const el = document.querySelector(hash)
        if (el) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - 100
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
        }
      })
    },

    destroySwipers() {
      destroySwiper(this.honorsSwiper)
      this.honorsSwiper = null
      this.swipers.forEach(destroySwiper)
      this.swipers = []
    },

    stopRdAutoplay() {
      if (this.rdAutoplayTimer) {
        window.clearInterval(this.rdAutoplayTimer)
        this.rdAutoplayTimer = null
      }
    },

    startRdAutoplay() {
      this.stopRdAutoplay()
      if (this.rdSlides.length <= 1) return
      this.rdAutoplayTimer = window.setInterval(() => {
        this.onRdNav('next', true)
      }, 5000)
    },

    onRdNav(direction, fromAutoplay) {
      const total = this.rdSlides.length
      if (total <= 1) return
      if (direction === 'next') {
        this.activeRdIndex = (this.activeRdIndex + 1) % total
      } else {
        this.activeRdIndex = (this.activeRdIndex - 1 + total) % total
      }
      if (!fromAutoplay) this.startRdAutoplay()
    },

    scheduleHonorsSwiperInit() {
      this.$nextTick(() => {
        window.setTimeout(() => this.initHonorsSwiper(), 80)
      })
    },

    initHonorsSwiper() {
      if (!this.$refs.aboutHonorsSwiper || !this.certificates.length) return
      destroySwiper(this.honorsSwiper)
      this.honorsSwiper = initAboutDiv8(this.$refs.aboutHonorsSwiper)
      if (this.honorsSwiper) {
        this.swipers = this.swipers.filter(item => item !== this.honorsSwiper)
        this.swipers.push(this.honorsSwiper)
      }
    },

    initSwipers() {
      this.scheduleHonorsSwiperInit()
    },

    async loadStats() {
      try {
        const res = await listSiteStat({ status: 1, pageNum: 1, pageSize: 10 }, this.$i18n.locale)
        const mapped = mapSiteStats(unwrapRows(res))
        if (mapped.length) {
          this.stats = mapped
        }
      } catch (e) { /* fallback */ }
    },

    applyHistory(row) {
      if (!row) return
      if (row.title) this.sectionTitles.history = this.$cleanBrand(row.title)
      const stages = parseHistoryStages(row.content)
      if (stages && stages.length) {
        this.historyStages = stages
        this.activeHistoryStage = 0
        this.activeHistoryYear = 0
      }
    },

    applyCulture(row) {
      if (!row) return
      if (row.title) this.sectionTitles.culture = this.$cleanBrand(row.title)
      const items = parseCultureLines(row.content)
      const imgs = parsePicUrlList(row.picUrl)
      if (items && items.length) {
        this.cultureItems = items.map((item, i) => ({
          ...item,
          image: imgs[i] ? resolveImageUrl(imgs[i]) : ''
        }))
      }
    },

    applyRdSection(row) {
      if (!row) return
      if (row.title) this.sectionTitles.rd = this.$cleanBrand(row.title)

      const content = String(row.content || '')
      const introMatch = content.match(/<p[^>]*>([\s\S]*?)<\/p>/i)
      if (introMatch) {
        this.rdContentText = this.stripHtml(introMatch[1])
      }

      const panelSource = content.replace(/^[\s\S]*?<\/p>/i, '').trim() || content
      const panelLines = parseManufacturingPanels(panelSource)
      const imgs = parsePicUrlList(row.picUrl)
      this.rdSlides = this.buildRdSlides(imgs, panelLines)
      this.activeRdIndex = 0
      this.$nextTick(() => this.startRdAutoplay())
    },

    buildRdSlides(cmsImages, panelLines) {
      if (!cmsImages || !cmsImages.length) return []
      return cmsImages.map((image, i) => ({
        title: this.$cleanBrand((panelLines && panelLines[i] && panelLines[i].title) || ''),
        image: resolveImageUrl(image)
      }))
    },

    resetCmsSections() {
      this.introContent = ''
      this.introSubtitleText = ''
      this.honorsContent = ''
      this.rdContentText = ''
      this.careersIntro = ''
      this.jobItems = []
      this.sectionTitles = {}
      this.historyStages = []
      this.cultureItems = []
      this.rdSlides = []
      this.activeRdIndex = 0
      this.stopRdAutoplay()
      this.activeHistoryStage = 0
      this.activeHistoryYear = 0
    },

    async loadCompanyByType(type) {
      try {
        const res = await listCompanyInfo({ type, status: 1, pageNum: 1, pageSize: 1 }, this.$i18n.locale)
        return unwrapRows(res)[0] || null
      } catch (e) {
        return null
      }
    },

    async loadCompanyListByType(type, pageSize = 50) {
      try {
        const res = await listCompanyInfo({ type, status: 1, pageNum: 1, pageSize }, this.$i18n.locale)
        return unwrapRows(res)
      } catch (e) {
        return []
      }
    },

    applyCareers(rows) {
      this.careersIntro = ''
      this.jobItems = []
      if (!rows || !rows.length) return

      if (rows.length > 1) {
        const sectionPattern = /^(招贤纳士|招聘|careers?|join us)$/i
        const sectionIdx = rows.findIndex(r => sectionPattern.test(this.stripHtml(this.$cleanBrand(r.title || ''))))
        if (sectionIdx >= 0) {
          const sectionRow = rows[sectionIdx]
          this.sectionTitles.careers = this.$cleanBrand(sectionRow.title)
          if (sectionRow.content && !parseJobItemsFromHtml(sectionRow.content).length) {
            this.careersIntro = sectionRow.content
          }
          this.jobItems = rows
            .filter((_, i) => i !== sectionIdx)
            .map(mapCompanyInfoRowToJob)
            .filter(Boolean)
        } else {
          this.jobItems = rows.map(mapCompanyInfoRowToJob).filter(Boolean)
        }
        return
      }

      const row = rows[0]
      if (row.title) this.sectionTitles.careers = this.$cleanBrand(row.title)
      const parsed = parseJobItemsFromHtml(row.content)
      if (parsed.length) {
        this.jobItems = parsed
        return
      }
      if (row.content) this.careersIntro = row.content
    },

    stripHtml(html) {
      if (!html) return ''
      return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
    },

    async load() {
      this.stopRdAutoplay()
      this.destroySwipers()
      this.resetCmsSections()
      await Promise.all([
        this.loadAboutNav(),
        this.loadStats(),
        this.loadCompanyByType(0).then(row => {
          if (!row) return
          this.introContent = row.content || ''
          if (row.title) {
            this.sectionTitles.intro = this.$cleanBrand(row.title)
          }
          const subtitleMatch = String(row.content || '').match(/<p[^>]*class="[^"]*subtitle[^"]*"[^>]*>([\s\S]*?)<\/p>/i)
          if (subtitleMatch) {
            this.introSubtitleText = this.stripHtml(subtitleMatch[1])
          }
        }),
        this.loadCompanyByType(2).then(row => this.applyHistory(row)),
        this.loadCompanyByType(3).then(row => this.applyCulture(row)),
        this.loadCompanyByType(4).then(row => this.applyRdSection(row)),
        this.loadCompanyByType(7).then(row => {
          if (!row) return
          if (row.title) this.sectionTitles.honors = this.$cleanBrand(row.title)
          if (row.content) this.honorsContent = row.content
        }),
        listCertificate({ status: 1, pageNum: 1, pageSize: 50 }, this.$i18n.locale).then(res => {
          this.certificates = unwrapRows(res)
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
            .map(item => ({
              id: item.id,
              title: this.$cleanBrand(item.title || ''),
              image: resolveImageUrl(item.picUrl || item.pic_url || '')
            }))
            .filter(item => item.image)
        }).catch(() => { this.certificates = [] }),
        this.loadCompanyListByType(6).then(rows => this.applyCareers(rows))
      ])
      await this.$nextTick()
      this.initSwipers()
      this.scrollToHash()
    }
  }
}
</script>

<style scoped>
.careers-intro {
  margin-bottom: 24px;
  line-height: 1.8;
  color: #666;
  text-align: center;
}
</style>
